import React, { useEffect, useState } from "react";
import SideBar from "../Component/SideBar";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Paper,
  Chip,
  Alert,
  Collapse,
  IconButton,
  Pagination,
  Divider,
} from "@mui/material";
import {
  postExamData,
  fetchExamDetailsById,
  putExamData,
  fetchSubjects,
} from "../Api/examApi";
import { useNavigate, useParams } from "react-router-dom";
import {
  Settings,
  QuestionAnswer,
  ExpandMore,
  ExpandLess,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export default function CreateQuestionBank() {
  const today = new Date().toISOString().split("T")[0];
  const [activeTab, setActiveTab] = useState(0);
  const [formValues, setFormValues] = useState({
    name: "",
    subject: "",
    topic: "",
    language: "",
    designedBy: "",
    testDate: "",
    validTillDate: "",
    durationInMinutes: "",
    totalQuestions: "",
    instructions: `This is an online multiple-choice test. Read all instructions carefully before beginning the test.
1. Once you start the test, the timer will begin. You must submit the test before time runs out.
2. Do not refresh or close your browser during the test. Doing so may result in automatic submission or disqualification.
3. Do not open other tabs, windows, or applications during the test. Suspicious activity may be flagged as malpractice.
4. Use of calculators, books, or external help is strictly prohibited, unless otherwise specified.
5. Internet connection must be stable. The test platform is not responsible for connectivity issues.
6. Attempt all questions. Unanswered questions will receive no marks.`,
    marksPerQuestion: "",
    marksCorrectAnswer: "",
    marksWrongAnswer: "",
    passingMarks: "",
    status: "1",
  });

  const [formErrors, setFormErrors] = useState({});
  const [questionErrors, setQuestionErrors] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [tabValidationError, setTabValidationError] = useState("");
  const [expandedQuestions, setExpandedQuestions] = useState(new Set([0])); // First question expanded by default
  const [questionsPerPage, setQuestionsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link"],
      [{ indent: "-1" }, { indent: "+1" }],
    ],
  };

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "list",
    "bullet",
    "align",
    "link",
    "indent",
  ];

  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const data = await fetchSubjects();
        setSubjects(data || []);
      } catch (error) {
        console.error("Error loading subjects:", error);
      }
    };
    loadSubjects();
  }, []);

  useEffect(() => {
    const loginData = JSON.parse(localStorage.getItem("loginData"));
    if (loginData?.student) {
      setFormValues((prev) => ({
        ...prev,
        designedBy: `${loginData.student.firstName} ${loginData.student.lastName}`,
      }));
    }
  }, []);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        const data = await fetchExamDetailsById(id);
        const gi = data?.generalInformation || {};
        const ms = data?.markingScheme || {};

        setFormValues({
          name: gi.name || "",
          subject: gi.subject || "",
          topic: gi.topic || "",
          language: gi.language || "",
          designedBy: gi.designedBy || "",
          testDate: gi.testDate?.split("T")[0] || "",
          validTillDate: gi.validTillDate?.split("T")[0] || "",
          durationInMinutes: gi.durationInMinutes || "",
          totalQuestions: gi.totalQuestions || "",
          instructions: gi.instructions || formValues.instructions,
          marksPerQuestion: ms.marksPerQuestion || "",
          marksCorrectAnswer: ms.marksCorrectAnswer || "",
          marksWrongAnswer:
            ms.marksWrongAnswer !== undefined
              ? ms.marksWrongAnswer.toString()
              : "",
          passingMarks:
            ms.passingMarks !== undefined ? ms.passingMarks.toString() : "",
          status: gi.status?.toString() || "1",
        });

        setQuestions(
          data.questionInformation?.questions?.map((q) => ({
            question: q.question,
            options: q.options.map((opt) => ({
              answer: opt.answer,
              isCorrect: opt.isCorrect,
              explanation: opt.explanation || "",
            })),
            solution: q.explanation || "",
          })) || []
        );
      } catch (error) {
        console.error("Error loading exam data:", error);
      }
    };

    fetchData();
  }, [id]);

  const canActivateStatus = () => {
    const totalQ = parseInt(formValues.totalQuestions);
    if (!totalQ || questions.length !== totalQ) return false;

    return questions.every((q) => {
      const hasMinOptions = q.options.length >= 2;
      const hasCorrect = q.options.some((opt) => opt.isCorrect);
      return q.question.trim() && hasMinOptions && hasCorrect;
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const numericFields = [
      "durationInMinutes",
      "totalQuestions",
      "marksPerQuestion",
      "marksCorrectAnswer",
      "status",
    ];

    if (name === "passingMarks") {
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        [name]:
          numericFields.includes(name) && value !== "" ? Number(value) : value,
      }));
    }

    setFormErrors((prev) => ({ ...prev, [name]: "" }));
    setTabValidationError(""); // Clear tab validation error when user starts typing
  };

  const handleStatusChange = (e) => {
    const value = Number(e.target.value);
    if (value === 2 && !canActivateStatus()) {
      setFormErrors((prev) => ({
        ...prev,
        status:
          "Activation is only allowed when all questions are properly configured: each must include a minimum of two options, at least one correct answer, and match the total question count entered.",
      }));
      return;
    }
    setFormValues((prev) => ({ ...prev, status: value }));
    setFormErrors((prev) => ({ ...prev, status: "" }));
  };

  const validateBasicInformation = () => {
    const errors = {};

    // Required string fields validation
    const requiredStringFields = [
      "name",
      "subject",
      "topic",
      "language",
      "designedBy",
      "testDate",
      "validTillDate",
    ];

    requiredStringFields.forEach((field) => {
      if (!formValues[field] || formValues[field].toString().trim() === "") {
        const fieldName = field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
        errors[field] = `${fieldName} is required`;
      }
    });

    // Required numeric fields validation
    const requiredNumericFields = [
      "durationInMinutes",
      "totalQuestions",
      "marksPerQuestion",
      "marksCorrectAnswer",
      "marksWrongAnswer",
    ];

    requiredNumericFields.forEach((field) => {
      if (
        formValues[field] === "" ||
        formValues[field] === null ||
        formValues[field] === undefined
      ) {
        const fieldName = field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
        errors[field] = `${fieldName} is required`;
      }
    });

    // Date validation
    if (formValues.testDate && formValues.validTillDate) {
      const testDate = new Date(formValues.testDate);
      const validTillDate = new Date(formValues.validTillDate);

      if (testDate < new Date(today))
        errors.testDate = "Test Date cannot be in the past.";
      if (validTillDate <= testDate)
        errors.validTillDate = "Valid Till Date must be later than Test Date.";
    }

    // Numeric value validation
    const marksPer = parseFloat(formValues.marksPerQuestion);
    if (!isNaN(marksPer)) {
      if (marksPer < 0) errors.marksPerQuestion = "Negative marks not allowed";
      else if (marksPer === 0) errors.marksPerQuestion = "0 marks not allowed";
    }

    const marksCorrect = parseFloat(formValues.marksCorrectAnswer);
    if (!isNaN(marksCorrect)) {
      if (marksCorrect < 0)
        errors.marksCorrectAnswer = "Negative marks not allowed";
      else if (marksCorrect === 0)
        errors.marksCorrectAnswer = "0 marks not allowed";
    }

    const duration = parseFloat(formValues.durationInMinutes);
    if (!isNaN(duration) && duration <= 0)
      errors.durationInMinutes = "Duration must be greater than 0";

    const totalQ = parseFloat(formValues.totalQuestions);
    if (!isNaN(totalQ) && totalQ <= 0)
      errors.totalQuestions = "Total Questions must be greater than 0";

    // Passing marks validation (optional field)
    const passingValue = formValues.passingMarks.trim();
    if (passingValue !== "") {
      const passing = parseFloat(passingValue);
      const totalPossibleMarks =
        parseFloat(formValues.totalQuestions) *
        parseFloat(formValues.marksCorrectAnswer);

      if (isNaN(passing)) {
        errors.passingMarks = "Please enter a valid number";
      } else if (passing < 0) {
        errors.passingMarks = "Passing score cannot be negative";
      } else if (passing > totalPossibleMarks) {
        errors.passingMarks = `Passing score cannot exceed total marks (${totalPossibleMarks})`;
      }
    }

    return errors;
  };

  const validateForm = () => {
    const errors = validateBasicInformation();
    const qErrors = [];

    questions.forEach((q, qIndex) => {
      const qErr = { question: "", options: [], correctAnswerError: "" };

      if (!q.question.trim()) qErr.question = "Question is required";

      let hasCorrect = false;
      q.options.forEach((opt, oIndex) => {
        qErr.options[oIndex] = "";
        if (!opt.answer.trim()) qErr.options[oIndex] = "Option is required";
        if (opt.isCorrect) hasCorrect = true;
      });

      if (q.options.length < 2)
        qErr.correctAnswerError = "At least 2 options required";
      else if (!hasCorrect)
        qErr.correctAnswerError = "Mark at least one correct answer";

      qErrors[qIndex] = qErr;
    });

    setFormErrors(errors);
    setQuestionErrors(qErrors);

    const isQuestionValid = qErrors.every(
      (q) =>
        !q.question &&
        !q.correctAnswerError &&
        q.options.every((optErr) => !optErr)
    );

    return Object.keys(errors).length === 0 && isQuestionValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitLoading(true);
    setSubmitError(null);

    const formattedQuestions = questions.map((q, qIndex) => ({
      questionId: qIndex + 1,
      question: q.question,
      IsMultipleCorrect: q.options.filter((opt) => opt.isCorrect).length > 1,
      options: q.options.map((opt, index) => ({
        id: String.fromCharCode(65 + index),
        answer: opt.answer,
        isCorrect: opt.isCorrect,
      })),
      explanation: q.solution || "",
    }));

    const passingMarksValue =
      formValues.passingMarks.trim() === ""
        ? 0
        : parseFloat(formValues.passingMarks);

    const payload = {
      generalInformation: {
        name: formValues.name,
        subject: formValues.subject,
        topic: formValues.topic,
        language: formValues.language,
        designedBy: formValues.designedBy,
        testDate: formValues.testDate
          ? new Date(formValues.testDate).toISOString()
          : "",
        validTillDate: formValues.validTillDate
          ? new Date(formValues.validTillDate).toISOString()
          : "",
        durationInMinutes: Number(formValues.durationInMinutes),
        totalQuestions: Number(formValues.totalQuestions),
        instructions: formValues.instructions,
        marksPerQuestion: Number(formValues.marksPerQuestion),
        marksCorrectAnswer: Number(formValues.marksCorrectAnswer),
        marksWrongAnswer: Number(formValues.marksWrongAnswer),
        passingMarks: passingMarksValue,
        status: Number(formValues.status),
      },
      markingScheme: {
        marksPerQuestion: Number(formValues.marksPerQuestion),
        marksCorrectAnswer: Number(formValues.marksCorrectAnswer),
        marksWrongAnswer: Number(formValues.marksWrongAnswer),
        passingMarks: passingMarksValue,
      },
      questionInformation: {
        allowMultipleCorrectAnswers: false,
        questions: formattedQuestions,
      },
    };

    try {
      if (id) await putExamData({ id, ...payload });
      else await postExamData(payload);
      alert(`Test ${id ? "updated" : "created"} successfully!`);
      navigate("/ListQuestionBank");
    } catch (error) {
      setSubmitError(error.message || "Error submitting form");
    } finally {
      setSubmitLoading(false);
    }
  };

  const addQuestion = () => {
    const total = parseInt(formValues.totalQuestions || 0);
    if (questions.length < total) {
      const newQuestionIndex = questions.length;
      setQuestions((prev) => [
        ...prev,
        {
          question: "",
          options: [{ answer: "", isCorrect: false }],
          solution: "",
        },
      ]);
      // Expand the new question by default
      setExpandedQuestions((prev) => new Set([...prev, newQuestionIndex]));

      // Navigate to the page containing the new question
      const newQuestionPage = Math.floor(newQuestionIndex / questionsPerPage);
      setCurrentPage(newQuestionPage);
    }
  };

  const toggleQuestionExpansion = (qIndex) => {
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(qIndex)) {
        newSet.delete(qIndex);
      } else {
        newSet.add(qIndex);
      }
      return newSet;
    });
  };

  const expandAllQuestions = () => {
    const currentPageQuestions = getPaginatedQuestions();
    const startIndex = currentPage * questionsPerPage;
    const pageQuestionIndices = currentPageQuestions.map(
      (_, index) => startIndex + index
    );

    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      pageQuestionIndices.forEach((index) => newSet.add(index));
      return newSet;
    });
  };

  const collapseAllQuestions = () => {
    const currentPageQuestions = getPaginatedQuestions();
    const startIndex = currentPage * questionsPerPage;
    const pageQuestionIndices = currentPageQuestions.map(
      (_, index) => startIndex + index
    );

    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      pageQuestionIndices.forEach((index) => newSet.delete(index));
      return newSet;
    });
  };

  const getPaginatedQuestions = () => {
    const startIndex = currentPage * questionsPerPage;
    const endIndex = startIndex + questionsPerPage;
    return questions.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(questions.length / questionsPerPage);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage - 1); // MUI Pagination is 1-based, our state is 0-based
  };

  const addOption = (qIndex) => {
    const updated = [...questions];
    updated[qIndex].options.push({ answer: "", isCorrect: false });
    setQuestions(updated);
  };

  const removeOption = (qIndex, oIndex) => {
    const updated = [...questions];
    if (updated[qIndex].options.length > 1)
      updated[qIndex].options.splice(oIndex, 1);
    setQuestions(updated);
  };

  const handleQuestionChange = (qIndex, value) => {
    const updated = [...questions];
    updated[qIndex].question = value;
    setQuestions(updated);
  };

  const handleOptionChange = (qIndex, oIndex, field, value) => {
    const updated = [...questions];
    updated[qIndex].options[oIndex][field] = value;
    setQuestions(updated);
  };

  const handleTabChange = (event, newValue) => {
    // If trying to go to Questions tab, validate basic information first
    if (newValue === 1) {
      const basicInfoErrors = validateBasicInformation();
      if (Object.keys(basicInfoErrors).length > 0) {
        setFormErrors(basicInfoErrors);
        setTabValidationError(
          "Please fill in all required basic information fields before proceeding to questions."
        );
        return; // Don't change tab
      } else {
        setTabValidationError(""); // Clear error if validation passes
      }
    }

    setActiveTab(newValue);
  };

  const handleNextButtonClick = () => {
    const basicInfoErrors = validateBasicInformation();
    if (Object.keys(basicInfoErrors).length > 0) {
      setFormErrors(basicInfoErrors);
      setTabValidationError(
        "Please fill in all required basic information fields before proceeding to questions."
      );
      return;
    }
    setTabValidationError("");
    setActiveTab(1);
  };

  // Get questions for current page with their original indices
  const paginatedQuestionsWithIndex = () => {
    const startIndex = currentPage * questionsPerPage;
    return getPaginatedQuestions().map((question, index) => ({
      ...question,
      originalIndex: startIndex + index,
    }));
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <SideBar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: { xs: "60px", sm: "55px" },
          width: { xs: "100%", sm: "calc(100% - 240px)" },
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          {id ? "Edit Test Paper" : "Create Test Paper"}
        </Typography>

        <Paper elevation={1} sx={{ mt: 3, overflow: "hidden" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                minHeight: { xs: 56, sm: 64 },
                padding: { xs: "8px 12px", sm: "12px 16px" },
              },
            }}
          >
            <Tab
              icon={<Settings />}
              iconPosition="start"
              label="BASIC INFORMATION"
              sx={{
                minHeight: { xs: 56, sm: 64 },
                "& .MuiTab-iconWrapper": {
                  marginBottom: { xs: 0, sm: "4px" },
                },
              }}
            />
            <Tab
              icon={<QuestionAnswer />}
              iconPosition="start"
              label={`QUESTIONS (${questions.length}/${
                formValues.totalQuestions || 0
              })`}
              sx={{
                minHeight: { xs: 56, sm: 64 },
                "& .MuiTab-iconWrapper": {
                  marginBottom: { xs: 0, sm: "4px" },
                },
              }}
            />
          </Tabs>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {tabValidationError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {tabValidationError}
              </Alert>
            )}

            {activeTab === 0 && (
              <Box>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  {/* Basic Info Section */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Test Name *"
                      value={formValues.name}
                      onChange={handleFormChange}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      fullWidth
                      name="subject"
                      label="Subject *"
                      value={formValues.subject}
                      onChange={handleFormChange}
                      error={!!formErrors.subject}
                      helperText={formErrors.subject}
                      size="medium"
                    >
                      {subjects.length === 0 && (
                        <MenuItem value="" disabled>
                          Loading subjects...
                        </MenuItem>
                      )}
                      {subjects.map((subj, index) => (
                        <MenuItem key={index} value={subj}>
                          {subj}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      name="topic"
                      label="Topic *"
                      value={formValues.topic}
                      onChange={handleFormChange}
                      error={!!formErrors.topic}
                      helperText={formErrors.topic}
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      name="language"
                      label="Language *"
                      value={formValues.language}
                      onChange={handleFormChange}
                      error={!!formErrors.language}
                      helperText={formErrors.language}
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      name="designedBy"
                      label="Designed By *"
                      value={formValues.designedBy}
                      InputProps={{ readOnly: true }}
                      error={!!formErrors.designedBy}
                      helperText={formErrors.designedBy}
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      select
                      name="status"
                      label="Status *"
                      value={formValues.status}
                      onChange={handleStatusChange}
                      error={!!formErrors.status}
                      helperText={formErrors.status}
                      size="medium"
                    >
                      <MenuItem value="1">In Progress</MenuItem>
                      <MenuItem value="2">Active</MenuItem>
                      <MenuItem value="3" disabled={!isEditMode}>
                        Inactive
                      </MenuItem>
                    </TextField>
                  </Grid>

                  {/* Schedule Section */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      name="testDate"
                      label="Test Date *"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: today }}
                      value={formValues.testDate}
                      onChange={handleFormChange}
                      error={!!formErrors.testDate}
                      helperText={formErrors.testDate}
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      name="validTillDate"
                      label="Valid Till Date *"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: formValues.testDate || today }}
                      value={formValues.validTillDate}
                      onChange={handleFormChange}
                      error={!!formErrors.validTillDate}
                      helperText={formErrors.validTillDate}
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      name="durationInMinutes"
                      label="Duration (Minutes) *"
                      value={formValues.durationInMinutes}
                      onChange={handleFormChange}
                      error={!!formErrors.durationInMinutes}
                      helperText={formErrors.durationInMinutes}
                      size="medium"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>

                  {/* Question Configuration */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      name="totalQuestions"
                      label="Total Questions *"
                      value={formValues.totalQuestions}
                      onChange={handleFormChange}
                      error={!!formErrors.totalQuestions}
                      helperText={formErrors.totalQuestions}
                      size="medium"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      name="marksPerQuestion"
                      label="Marks Per Question *"
                      value={formValues.marksPerQuestion}
                      onChange={handleFormChange}
                      error={!!formErrors.marksPerQuestion}
                      helperText={formErrors.marksPerQuestion}
                      size="medium"
                      inputProps={{ min: 0.1, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      name="passingMarks"
                      label="Passing Score"
                      value={formValues.passingMarks}
                      onChange={handleFormChange}
                      error={!!formErrors.passingMarks}
                      helperText={
                        formErrors.passingMarks || "Specify 0 if not required"
                      }
                      placeholder="0"
                      size="medium"
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      name="marksCorrectAnswer"
                      label="Marks Correct Answer *"
                      value={formValues.marksCorrectAnswer}
                      onChange={handleFormChange}
                      error={!!formErrors.marksCorrectAnswer}
                      helperText={formErrors.marksCorrectAnswer}
                      size="medium"
                      inputProps={{ min: 0.1, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      name="marksWrongAnswer"
                      label="Marks Wrong Answer *"
                      value={formValues.marksWrongAnswer}
                      onChange={handleFormChange}
                      error={!!formErrors.marksWrongAnswer}
                      helperText={formErrors.marksWrongAnswer}
                      size="medium"
                      inputProps={{ step: 0.1 }}
                    />
                  </Grid>

                  {/* Instructions */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      label="Instructions for Students"
                      name="instructions"
                      value={formValues.instructions}
                      onChange={handleFormChange}
                      error={!!formErrors.instructions}
                      helperText={formErrors.instructions}
                      placeholder="Enter instructions here"
                      size="medium"
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "center", sm: "flex-end" },
                    mt: 4,
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleNextButtonClick}
                    size="large"
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      maxWidth: { xs: "300px", sm: "none" },
                    }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "center" },
                    mb: 3,
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "primary.main",
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    Question Management
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <Chip
                      label={`Progress: ${questions.length}/${
                        formValues.totalQuestions || 0
                      } questions created`}
                      color={
                        questions.length === parseInt(formValues.totalQuestions)
                          ? "success"
                          : "default"
                      }
                      variant="outlined"
                      sx={{ order: { xs: 2, sm: 1 } }}
                    />
                    <Button
                      variant="contained"
                      onClick={addQuestion}
                      disabled={
                        questions.length >=
                        parseInt(formValues.totalQuestions || 0)
                      }
                      sx={{
                        order: { xs: 1, sm: 2 },
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      + ADD QUESTION
                    </Button>
                  </Box>
                </Box>

                {questions.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "center" },
                        mb: 1,
                        gap: 2,
                        p: 1,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Questions {currentPage * questionsPerPage + 1} -{" "}
                        {Math.min(
                          (currentPage + 1) * questionsPerPage,
                          questions.length
                        )}{" "}
                        of {questions.length}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={expandAllQuestions}
                          startIcon={<ExpandMore />}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          Expand All
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={collapseAllQuestions}
                          startIcon={<ExpandLess />}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          Collapse All
                        </Button>
                      </Box>
                    </Box>

                    {getTotalPages() > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      >
                        <Pagination
                          count={getTotalPages()}
                          page={currentPage + 1}
                          onChange={handlePageChange}
                          color="primary"
                          size="medium"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {paginatedQuestionsWithIndex().map((questionData) => {
                  const { originalIndex } = questionData;
                  const q = questions[originalIndex];
                  const isExpanded = expandedQuestions.has(originalIndex);

                  return (
                    <Paper
                      key={originalIndex}
                      elevation={2}
                      sx={{
                        mb: 1,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      {/* Question Header */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: { xs: 1, sm: 1 },
                          backgroundColor: isExpanded
                            ? "#f8f9fa"
                            : "transparent",
                          cursor: "pointer",
                          borderBottom: isExpanded
                            ? "1px solid #e0e0e0"
                            : "none",
                        }}
                        onClick={() => toggleQuestionExpansion(originalIndex)}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: "primary.main",
                            fontSize: { xs: "1rem", sm: "1rem" },
                            fontWeight: 600,
                          }}
                        >
                          Question {originalIndex + 1}
                          {q.question && (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                ml: 1,
                                color: "text.secondary",
                                fontWeight: 200,
                                display: { xs: "none", sm: "inline" },
                              }}
                            >
                              ({q.options.length} options,{" "}
                              {q.options.filter((opt) => opt.isCorrect).length}{" "}
                              correct)
                            </Typography>
                          )}
                        </Typography>
                        <IconButton size="small">
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>

                      {/* Question Content */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ p: { xs: 1, sm: 2 } }}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, color: "text.secondary" }}
                            >
                              Enter your question
                            </Typography>

                            <Box
                              sx={{
                                border: "1px solid rgba(0, 0, 0, 0.23)",
                                borderRadius: "8px",
                                "&:hover": {
                                  borderColor: "black",
                                },
                                "&:focus-within": {
                                  borderColor: "#1976d2",
                                  boxShadow:
                                    "0 0 0 2px rgba(25, 118, 210, 0.2)",
                                },
                              }}
                            >
                              <ReactQuill
                                theme="snow"
                                value={q.question || ""}
                                onChange={(value) =>
                                  handleQuestionChange(originalIndex, value)
                                }
                                modules={{
                                  toolbar: [
                                    ["bold", "italic", "underline"],
                                    [{ list: "ordered" }, { list: "bullet" }],
                                    ["clean"],
                                  ],
                                }}
                                formats={[
                                  "bold",
                                  "italic",
                                  "underline",
                                  "list",
                                  "bullet",
                                ]}
                                style={{
                                  minHeight: "100px",
                                  maxHeight: "250px",
                                  overflowY: "auto",
                                  border: "none",
                                }}
                              />
                            </Box>

                            {questionErrors[originalIndex]?.question && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                {questionErrors[originalIndex].question}
                              </Typography>
                            )}
                          </Box>

                          {q.options.map((opt, oIndex) => (
                            <Grid
                              container
                              spacing={2}
                              key={oIndex}
                              alignItems="center"
                              sx={{ mb: 2 }}
                            >
                              <Grid item xs={12} sm={6} md={7}>
                                <TextField
                                  fullWidth
                                  label={`Option ${String.fromCharCode(
                                    65 + oIndex
                                  )}`}
                                  value={opt.answer}
                                  onChange={(e) =>
                                    handleOptionChange(
                                      originalIndex,
                                      oIndex,
                                      "answer",
                                      e.target.value
                                    )
                                  }
                                  error={
                                    !!questionErrors[originalIndex]?.options?.[
                                      oIndex
                                    ]
                                  }
                                  helperText={
                                    questionErrors[originalIndex]?.options?.[
                                      oIndex
                                    ] || ""
                                  }
                                  size="medium"
                                />
                              </Grid>
                              <Grid item xs={12} sm={3} md={2}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={opt.isCorrect}
                                      onChange={(e) =>
                                        handleOptionChange(
                                          originalIndex,
                                          oIndex,
                                          "isCorrect",
                                          e.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label="Correct"
                                  sx={{
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: { xs: "0.875rem", sm: "1rem" },
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={3}
                                md={3}
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexDirection: { xs: "row", sm: "row" },
                                  justifyContent: {
                                    xs: "space-between",
                                    sm: "flex-start",
                                  },
                                }}
                              >
                                <Button
                                  onClick={() =>
                                    removeOption(originalIndex, oIndex)
                                  }
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  disabled={q.options.length <= 1}
                                  sx={{
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    minWidth: { xs: "auto", sm: "64px" },
                                    flex: { xs: 1, sm: "none" },
                                  }}
                                >
                                  Remove
                                </Button>
                                <Button
                                  onClick={() => addOption(originalIndex)}
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  sx={{
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    minWidth: { xs: "auto", sm: "64px" },
                                    flex: { xs: 1, sm: "none" },
                                  }}
                                >
                                  Add Option
                                </Button>
                              </Grid>
                            </Grid>
                          ))}

                          <Typography
                            variant="subtitle1"
                            sx={{
                              mt: 1,
                              fontWeight: 600,
                              fontSize: { xs: "1rem", sm: "1.1rem" },
                            }}
                          >
                            Explanation (Optional)
                          </Typography>
                          <ReactQuill
                            theme="snow"
                            value={q.solution || ""}
                            onChange={(value) => {
                              const updated = [...questions];
                              updated[originalIndex].solution = value;
                              setQuestions(updated);
                            }}
                            modules={quillModules}
                            formats={quillFormats}
                            style={{
                              minHeight: "100px",
                              maxHeight: "250px",
                              overflowY: "auto",
                            }}
                          />

                          {questionErrors[originalIndex]
                            ?.correctAnswerError && (
                            <Typography
                              color="error"
                              variant="body2"
                              sx={{
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                              }}
                            >
                              {questionErrors[originalIndex].correctAnswerError}
                            </Typography>
                          )}

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              justifyContent: { xs: "center", sm: "flex-end" },
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={addQuestion}
                              disabled={
                                questions.length >=
                                parseInt(formValues.totalQuestions || 0)
                              }
                              size="small"
                              sx={{
                                width: { xs: "100%", sm: "auto" },
                                order: { xs: 1, sm: 1 },
                              }}
                            >
                              + ADD QUESTION
                            </Button>

                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => {
                                const updated = [...questions];
                                updated.splice(originalIndex, 1);
                                setQuestions(updated);

                                // Adjust current page if necessary
                                const newTotalPages = Math.ceil(
                                  (questions.length - 1) / questionsPerPage
                                );
                                if (
                                  currentPage >= newTotalPages &&
                                  newTotalPages > 0
                                ) {
                                  setCurrentPage(newTotalPages - 1);
                                }
                              }}
                              size="small"
                              sx={{
                                width: { xs: "100%", sm: "auto" },
                                order: { xs: 2, sm: 2 },
                              }}
                            >
                              Delete Question
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}

                {questions.length > 0 && getTotalPages() > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 3,
                      mb: 2,
                    }}
                  >
                    <Pagination
                      count={getTotalPages()}
                      page={currentPage + 1}
                      onChange={handlePageChange}
                      color="primary"
                      size="medium"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "center" },
                    mt: 4,
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setActiveTab(0)}
                    size="large"
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      order: { xs: 2, sm: 1 },
                    }}
                  >
                    Back to Basic Info
                  </Button>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "center", sm: "flex-end" },
                      gap: 2,
                      order: { xs: 1, sm: 2 },
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={submitLoading || questions.length === 0}
                      size="large"
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: "120px" },
                      }}
                    >
                      {submitLoading
                        ? "Submitting..."
                        : id
                        ? "Update Test"
                        : "Save Test"}
                    </Button>
                  </Box>
                </Box>

                {submitError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {submitError}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
